// eslint-disable-next-line unused-imports/no-unused-imports
import { action, computed, makeAutoObservable } from 'mobx';
import type { AuthTokens } from '@uplift/types';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from '@uplift/services';
import type { RootStore } from '../root/root';
import { authRefreshToken, authUpdateLastLogin } from '@uplift/api';
import jwtDecode from 'jwt-decode';

const makeUrl = (loc: Location, params: URLSearchParams): URL => {
  const urlPart = `${loc.origin}${loc.pathname}`;
  const paramsString = params.toString();

  if (!paramsString) {
    return new URL(urlPart);
  }

  return new URL(`${urlPart}?${paramsString}`);
};

export class AuthStore {
  tokens: AuthTokens = {
    accessToken: null,
    refreshToken: null,
  };
  loading = true;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  @action getTokens() {
    const oldTokens = { ...this.tokens };

    this.tokens = {
      accessToken: oldTokens.accessToken,
      refreshToken:
        oldTokens?.refreshToken ??
        LocalStorageService.getItem(LOCAL_STORAGE_KEYS.refreshToken),
    };
  }

  @action setLoading(loading: boolean) {
    this.loading = loading;
  }

  @computed
  get hasAccessToken(): boolean {
    return Boolean(this.tokens.accessToken);
  }

  @computed
  get hasRefreshToken(): boolean {
    return Boolean(this.tokens.refreshToken);
  }

  @computed
  get isExpired(): boolean {
    const refreshToken = this.tokens.refreshToken
      || LocalStorageService.getItem(LOCAL_STORAGE_KEYS.refreshToken);

    if (!refreshToken) {
      return true;
    }

    const decodedToken = jwtDecode<{ exp: number }>(refreshToken);
    const currentTime = Math.floor(Date.now() / 1000);

    return decodedToken.exp < currentTime;
  }

  @computed
  get isLoggedIn(): boolean {
    return (
      Boolean(this.tokens.refreshToken) ||
      Boolean(LocalStorageService.getItem(LOCAL_STORAGE_KEYS.refreshToken))
    );
  }

  @computed
  get needToFetchAccessToken(): boolean {
    return !this.hasAccessToken && this.hasRefreshToken;
  }

  @action
  setTokens(tokens: AuthTokens) {
    this.tokens = { ...tokens };
  }

  @action
  setTokensInLocalStorage() {
    LocalStorageService.setItem('refreshToken', this.tokens.refreshToken);
  }

  @action
  async tryRefresh(
    loc: Location,
    redirectUrl: string,
    onAuthorized: () => void
  ) {
    const params = new URLSearchParams(loc.search);

    try {
      if (this.isLoggedIn && !this.isExpired && params.has('auth_token')) {
        params.delete('auth_token');

        const newUrl = makeUrl(window.location, params);

        window.history.pushState({}, null, newUrl);
        onAuthorized();
        this.loading = false;

        return;
      }

      if (params.has('auth_token')) {
        this.tokens.refreshToken = params.get('auth_token');

        try {
          await this.fetchAccessTokenByRefreshToken();
        } catch (e) {
          console.error(e);
        }

        if (this.isLoggedIn) {
          params.delete('auth_token');

          if (!params.has('impersonate')) {
            await authUpdateLastLogin();
          }

          window.history.pushState({}, null, makeUrl(window.location, params));
        } else {
          this.logout(redirectUrl);
        }
      }

      if (!this.isLoggedIn) {
        this.logout(redirectUrl, { verificationToken: params.get('email_verification_code')?.toString() });
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  @action
  async fetchAccessTokenByRefreshToken() {
    const tokens = await authRefreshToken(this.tokens.refreshToken);

    if (tokens) {
      this.setTokens({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });
      this.setTokensInLocalStorage();
    } else {
      this.clear();
    }
  }

  @action
  clear() {
    this.tokens = {};

    LocalStorageService.clear();
  }

  private isRedirectingAfterLogout = false;
  logout(
    redirectUrl?: string,
    opts?: {
      redirectAfterLogin?: string | boolean;
      showAuthPrivacyError?: boolean;
      onBeforeRedirect?: () => void;
      verificationToken?: string;
      searchParams?: URLSearchParams;
    },
  ) {
    this.clear();

    if (redirectUrl) {
      if (this.isRedirectingAfterLogout) return;
      this.isRedirectingAfterLogout = true;

      opts?.onBeforeRedirect?.();

      const finalRedirectUrl = new URL(redirectUrl);

      if (opts?.redirectAfterLogin) {
        const _redirectAfterLogin = opts?.redirectAfterLogin === true
          ? window.location.pathname
          : opts?.redirectAfterLogin;

        finalRedirectUrl.searchParams.append('redirect_to', _redirectAfterLogin);
      }

      if (opts?.showAuthPrivacyError) {
        finalRedirectUrl.searchParams.append('privacy_alert', '1');
        opts?.searchParams?.delete('privacy_alert');
      }

      if (opts?.searchParams) {
        for (const [ key, value ] of opts.searchParams.entries()) {
          finalRedirectUrl.searchParams.append(key, value);
        }
      }

      // .replace() will replace current url with new one,
      // so it will be not saved in the history and back button will be working incorrectly
      // this will redirect user to the url
      window.location.href = finalRedirectUrl.toString();
    }
  }
}

export default AuthStore;
